import React, { useState, useEffect } from 'react';
import { useBookDetails } from '../hooks/useBookDetails';
import { useBookRating } from '../hooks/useBookRating';
import { useFormattedDescription } from '../hooks/useFormattedDescription';
import './MyListView.css';

const MIN_RATINGS = 25;
const DRAWER_ANIM_MS = 280;

export default function MyListView({ myList, setMyList, onBack, onDiscover }) {
  const [selectedBook, setSelectedBook] = useState(null);
  const [closing, setClosing] = useState(false);

  function removeBook(key) {
    setMyList(prev => prev.filter(b => b.key !== key));
  }

  function closeDrawer() {
    setClosing(true);
    setTimeout(() => { setSelectedBook(null); setClosing(false); }, DRAWER_ANIM_MS);
  }

  // Esc closes the drawer
  useEffect(() => {
    if (!selectedBook) return;
    function onKey(e) { if (e.key === 'Escape') closeDrawer(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBook]);

  return (
    <div className="mylist-view">
      <header className="mylist-topbar">
        <div className="mylist-topbar-left">
          <button className="mylist-back-btn" onClick={onBack}>
            <i className="ph ph-arrow-left" /> Back
          </button>
          <span className="mylist-heading">
            My List
            <span className="mylist-count">{myList.length > 0 ? myList.length : ''}</span>
          </span>
        </div>
        <span className="mylist-wordmark">
          <img src="/matchbook-logo.svg" alt="Matchbook" className="wordmark-logo" />
        </span>
      </header>

      <main className="mylist-main">
        {myList.length === 0 ? (
          <div className="mylist-empty">
            <div className="mylist-empty__icon">
              <i className="ph ph-bookmarks" />
            </div>
            <h2 className="mylist-empty__heading">Nothing saved yet</h2>
            <p className="mylist-empty__sub">
              Save books while you're discovering and they'll show up here.
            </p>
            <button className="mylist-empty__cta" onClick={onDiscover}>
              <i className="ph ph-sparkle" /> Keep discovering
            </button>
          </div>
        ) : (
          <div className="mylist-grid">
            {myList.map(book => (
              <div
                className="mylist-card"
                key={book.key}
                onClick={() => setSelectedBook(book)}
                role="button"
                tabIndex={0}
                onKeyDown={e => {
                  if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setSelectedBook(book); }
                }}
              >
                <div className="mylist-card-cover">
                  {book.coverUrl ? (
                    <img
                      src={book.coverUrl.replace('-M.jpg', '-L.jpg')}
                      alt={book.title}
                      draggable={false}
                    />
                  ) : (
                    <div className="mylist-card-fallback">{book.title}</div>
                  )}
                  <button
                    className="mylist-card-remove"
                    onClick={e => { e.stopPropagation(); removeBook(book.key); }}
                    aria-label={`Remove ${book.title}`}
                    title="Remove from list"
                  >
                    <i className="ph ph-x" />
                  </button>
                </div>
                <div>
                  <p className="mylist-card-title">{book.title}</p>
                  <p className="mylist-card-author">{book.author}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {selectedBook && (
        <BookDetailDrawer book={selectedBook} closing={closing} onClose={closeDrawer} />
      )}
    </div>
  );
}

/* ── Slide-up detail drawer ── */
function BookDetailDrawer({ book, closing, onClose }) {
  const { details } = useBookDetails(book);
  const hcRating = useBookRating(book);

  const rawDescription = book.description || details?.description || null;
  const bookForFmt = rawDescription ? { ...book, description: rawDescription } : null;
  const { formatted, loading: descLoading } = useFormattedDescription(bookForFmt);
  const description = formatted ?? rawDescription;

  const showRating = hcRating?.ratingsCount >= MIN_RATINGS;
  const q = encodeURIComponent(`${book.title} ${book.author}`);
  const paragraphs = description
    ? description.split(/\n+/).map(p => p.trim()).filter(Boolean)
    : [];

  return (
    <div
      className={`drawer-overlay${closing ? ' drawer-overlay--closing' : ''}`}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className={`drawer${closing ? ' drawer--closing' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={book.title}
      >
        <button className="drawer__handle" onClick={onClose} aria-label="Close details" />

        <div className="drawer__scroll">
          <div className="drawer__head">
            <div className="drawer__cover">
              {book.coverUrl ? (
                <img src={book.coverUrl.replace('-M.jpg', '-L.jpg')} alt={book.title} draggable={false} />
              ) : (
                <div className="drawer__cover-fallback">{book.title}</div>
              )}
            </div>

            <div className="drawer__headinfo">
              <h2 className="drawer__title">{book.title}</h2>
              <p className="drawer__author">{book.author}</p>

              <div className="drawer__meta">
                {book.year && <span>{book.year}</span>}
                {details?.pageCount && <span>{details.pageCount} pages</span>}
                {showRating && (
                  <span className="drawer__rating">
                    ★ {hcRating.rating.toFixed(1)}
                    <span className="drawer__rating-count">{hcRating.ratingsCount.toLocaleString()}</span>
                  </span>
                )}
              </div>

              <div className="drawer__buys">
                <a className="drawer__buy" href={`https://bookshop.org/search?keywords=${q}`} target="_blank" rel="noopener noreferrer">
                  <i className="ph ph-storefront" /> Bookshop.org
                </a>
                <a className="drawer__buy" href={`https://www.thriftbooks.com/browse/?b.search=${q}`} target="_blank" rel="noopener noreferrer">
                  <i className="ph ph-books" /> ThriftBooks
                </a>
                <a className="drawer__buy" href={`https://search.worldcat.org/search?q=${q}`} target="_blank" rel="noopener noreferrer">
                  <i className="ph ph-library" /> Find in a library
                </a>
              </div>
            </div>
          </div>

          {book.matchReason && <p className="drawer__match">{book.matchReason}</p>}

          {paragraphs.length > 0 && (
            <div className="drawer__desc">
              {paragraphs.map((para, i) => <p key={i}>{para}</p>)}
            </div>
          )}
          {descLoading && paragraphs.length === 0 && (
            <p className="drawer__loading">Loading details…</p>
          )}
        </div>
      </div>
    </div>
  );
}
